<template>
  <div class="form-property" :class="{ 'has-error': hasError }">
    <label>
      
      {{ label }}
      
      <Quickinfo
        v-if="quickinfo"
        :item="{ quickinfo: quickinfo }"
      />
    </label>

    <p
      class="form-error"
      v-show="hasError"
      v-html="errorMsg"
    ></p>

    <input
      type="text"
      ref="input"
      v-model="placesSearch"
      name="placesSearch"
      autocomplete="off"
      data-matomo-mask
      @focus="onFocus"
      @keydown="onKeyDown"
    />

    <ul class="google-predictions" :class="{ 'is-active': listActive }">
      <li
        v-for="(result, i) in searchResults"
        :key="i"
        @click="onSearchResultClicked(result)"
      >
        {{ result.placePrediction.text.text }}
      </li>
    </ul>
  </div>
</template>

<script>
import { EventBus } from "@/bus/eventbus.js";
import GooglePlacesController from "@/controller/GooglePlacesController";
import { Quickinfo } from "@/components/";

export default {
  name: "GoogleAutoComplete",
  
  components: {
    Quickinfo
  },
  
  props: {
    quickinfo: {
      type: [Object, String],
      default: null
    },
    label: {
      type: String,
      default: ""
    },
    errorText: {
      type: String,
      default: ""
    },
    storedValue: {
      type: String,
      default: ""
    },
    params: {
      type: Object,
      default: () => ({})
    },
    countries: {
      type: Array,
      default: () => ["AT", "DE", "IT", "ES", "PL"]
    },
    requiredFields: {
      type: Array,
      default: () => []
    },
    debounceMs: {
      type: Number,
      default: 300
    }
  },

  data() {
    return {
      placesSearch: this.storedValue,
      searchResults: [],
      listActive: false,
      hasError: false,
      errorMsg: "",
      sessionToken: null,
      suppressSearch: false,
      lastEmittedValue: null,
      searchTimeout: null
    };
  },

  watch: {
    storedValue(v) {
      this.suppressSearch = true;
      this.placesSearch = v;
    },

    placesSearch(v) {
      if (this.suppressSearch) {
        this.suppressSearch = false;
        return;
      }

      if (!v) {
        this.lastEmittedValue = null;
        this.$emit("onReset");
        this.sessionToken = null;
        this.checkError();
      }

      this.search(v);
    }
  },

  created() {
    EventBus.$on("onFormError", this.checkError);
  },

  beforeDestroy() {
    EventBus.$off("onFormError", this.checkError);
    clearTimeout(this.searchTimeout);
  },

  methods: {
    onBlur() {
      // give click on prediction a chance to run before closing
      setTimeout(() => {
        this.listActive = false;
      }, 150);
    },
    ensureSessionToken() {
      if (!this.sessionToken) {
        this.sessionToken = crypto.randomUUID();
      }
    },

    async search(val) {
      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(async () => {
        if (!val || val.length < 1) {
          this.searchResults = [];
          this.listActive = false;
          return;
        }

        this.ensureSessionToken();

        const res = await GooglePlacesController.autocomplete(val, {
          fieldMask:
            "suggestions.placePrediction.placeId," +
            "suggestions.placePrediction.text",
          params: {
            includedRegionCodes: this.countries,
            ...this.params,
            sessionToken: this.sessionToken
          }
        });

        if (!res) {
          this.searchResults = [];
          return;
        }

        this.searchResults = res.suggestions || [];
        this.listActive =
          document.activeElement === this.$refs.input &&
          this.searchResults.length > 0;
      }, this.debounceMs);
    },

    async onSearchResultClicked(result) {
      this.listActive = false;

      const displayText = result.placePrediction.text.text;
      this.suppressSearch = true;
      this.placesSearch = displayText;

      this.ensureSessionToken();

      const detail = await GooglePlacesController.getPlaceDetails(
        result.placePrediction.placeId,
        {
          fieldMask: "formattedAddress,location,addressComponents",
          query: { sessionToken: this.sessionToken }
        }
      );

      if (!detail) return;

      const comp = detail.addressComponents || [];
      const getShort = t => comp.find(c => c.types.includes(t))?.shortText || "";
      const getLong = t => comp.find(c => c.types.includes(t))?.longText || "";

      const payload = {
        formattedAddress: detail.formattedAddress,
        displayAddress: displayText, // stays consistent with UI
        lat: detail.location?.latitude ?? "",
        lng: detail.location?.longitude ?? "",
        countryLong: getLong("country"),
        countryShort: getShort("country"),
        city:
          getShort("locality") ||
          getShort("postal_town") ||
          getShort("administrative_area_level_3") ||
          getShort("administrative_area_level_2") ||
          getShort("administrative_area_level_1"),
        postalCode: getShort("postal_code"),
        street: getLong("route"),
        houseNumber: getShort("street_number"),
      };

      this.lastEmittedValue = payload;
      this.$emit("onChange", payload);
      this.sessionToken = null;
      this.checkError();
    },

    onFocus() {
      this.ensureSessionToken();
    },

    onKeyDown(e) {
      if (e.key === "Enter") e.preventDefault();
    },

    checkError() {
      let hasError = false;

      if (!this.placesSearch) {
        hasError = true;
      }

      // if requiredFields are defined, a Google selection is mandatory
      if (this.requiredFields.length) {
        if (!this.lastEmittedValue) {
          hasError = true;
        } else {
          for (const field of this.requiredFields) {
            if (!this.lastEmittedValue[field]) {
              hasError = true;
              break;
            }
          }
        }
      }

      this.hasError = hasError;
      this.errorMsg = hasError ? this.errorText : "";

      this.$emit("onErrorChange", {
        field: "placesSearch",
        hasError: this.hasError,
        message: this.errorMsg
      });
    }
  }
};
</script>
